(() => {
  const audienceLabels = {
    apartment: 'Lägenhet',
    villa: 'Hus',
    brf: 'BRF',
    business: 'Butik / företag'
  };

  const steps = [
    {
      key: 'opening',
      title: 'Vilken typ av dörr har du?',
      lead: 'Välj det alternativ som passar bäst.',
      layout: 'opening',
      options: [
        { value: 'single', label: 'Enkeldörr', image: 'single' },
        { value: 'double', label: 'Pardörr', image: 'double' }
      ]
    },
    {
      key: 'security',
      title: 'Vilken säkerhetsklass vill du ha?',
      lead: 'Välj den nivå av inbrottsskydd som passar dina behov.',
      layout: 'security',
      note: 'Vi kontrollerar alltid klassning och installationsförutsättningar före beställning.',
      options: [
        { value: 'rc3', label: 'RC3', text: 'Högt inbrottsskydd för bostäder.', icon: 'rc3' },
        { value: 'rc4', label: 'RC4', text: 'Förstärkt skydd vid högre säkerhetskrav.', icon: 'rc4' }
      ]
    },
    {
      key: 'model',
      title: 'Vilket utförande vill du ha?',
      lead: 'Välj en av våra åtta dörrmodeller. Bilden anpassas efter enkel- eller pardörr.',
      layout: 'model',
      options: [
        { value: 'mahogany', label: 'Mahogny', image: 'mahogany' },
        { value: 'nordic_oak', label: 'Nordisk ek', image: 'nordic_oak' },
        { value: 'horizontal_oak', label: 'Liggande ek', image: 'horizontal_oak' },
        { value: 'golden_oak', label: 'Golden Oak', image: 'golden_oak' },
        { value: 'white_flat', label: 'Vit slät', image: 'white_flat' },
        { value: 'white_panelled', label: 'Speglad vit', image: 'white_panelled' },
        { value: 'grey_panelled', label: 'Speglad grå', image: 'grey_panelled' },
        { value: 'stained_oak_panelled', label: 'Speglad betsad ek', image: 'stained_oak_panelled' }
      ]
    },
    {
      key: 'hardware',
      title: 'Vilket beslag eller lås vill du ha?',
      lead: 'Välj standardbeslag, mässingsbeslag eller elektroniskt lås.',
      layout: 'hardware',
      options: [
        { value: 'standard', label: 'Standardbeslag', text: 'Ingår i dörrens grundpris.', image: 'standard' },
        { value: 'brass', label: 'Mässingsbeslag', text: 'Klassiskt utförande i mässing.', image: 'brass' },
        { value: 'electronic', label: 'Elektroniskt lås', text: 'Yale Doorman L3.', image: 'electronic' }
      ]
    }
  ];

  const formatSek = (value) => `${new Intl.NumberFormat('sv-SE', {
    maximumFractionDigits: 0
  }).format(Math.round(value))} kr`;

  const shieldMarkup = (type) => {
    const fill = type === 'rc4'
      ? '<path d="M32 8 51 15v14c0 13-8 23-19 28C21 52 13 42 13 29V15l19-7Z" fill="#27303b"/><path d="M32 13v38c8-5 14-12 14-22V19l-14-6Z" fill="#fff" opacity=".9"/>'
      : '<path d="M32 8 51 15v14c0 13-8 23-19 28C21 52 13 42 13 29V15l19-7Z" fill="#f1f1ee"/>';
    return `<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M32 6 53 14v15c0 14-8.8 25.4-21 31C19.8 54.4 11 43 11 29V14l21-8Z" fill="none" stroke="currentColor" stroke-width="3"/>${fill}</svg>`;
  };

  const initWizard = (hero) => {
    if (hero.dataset.daWizardInitialized === 'true') return;
    const dialog = hero.querySelector('[data-da-wizard]');
    const configNode = hero.querySelector('[data-da-wizard-config]');
    if (!dialog || !configNode) return;

    let config;
    try { config = JSON.parse(configNode.textContent); } catch (error) { return; }
    hero.dataset.daWizardInitialized = 'true';

    const el = {
      starts: hero.querySelectorAll('[data-da-wizard-start]'),
      guideFocus: hero.querySelector('[data-da-guide-focus]'),
      guidePanel: hero.querySelector('.da-premium-hero__guide'),
      close: dialog.querySelector('[data-da-wizard-close]'),
      progressItems: Array.from(dialog.querySelectorAll('[data-da-wizard-step]')),
      question: dialog.querySelector('[data-da-wizard-question]'),
      contact: dialog.querySelector('[data-da-wizard-contact]'),
      title: dialog.querySelector('[data-da-wizard-title]'),
      lead: dialog.querySelector('[data-da-wizard-lead]'),
      options: dialog.querySelector('[data-da-wizard-options]'),
      note: dialog.querySelector('[data-da-wizard-note]'),
      back: dialog.querySelector('[data-da-wizard-back]'),
      next: dialog.querySelector('[data-da-wizard-next]'),
      contactBack: dialog.querySelector('[data-da-wizard-contact-back]'),
      summary: dialog.querySelector('[data-da-wizard-summary]'),
      preview: dialog.querySelector('[data-da-wizard-preview]'),
      form: dialog.querySelector('.da-door-wizard__form'),
      errors: dialog.querySelector('[data-da-wizard-errors]'),
      vatControl: dialog.querySelector('.da-door-wizard__vat'),
      vatButtons: Array.from(dialog.querySelectorAll('[data-da-vat-mode]'))
    };

    const resultKey = 'da-door-wizard-result';
    const leadKey = 'da-door-wizard-lead-state';
    const defaultVatMode = config.defaultVatMode === 'excl' ? 'excl' : 'incl';
    const state = { audience: 'apartment', stepIndex: 0, answers: {}, vatMode: defaultVatMode };

    const imageFor = (step, option) => {
      if (!option.image) return '';
      if (step.key === 'opening') return config.openingImages?.[option.image] || '';
      if (step.key === 'model') return config.modelImages?.[state.answers.opening || 'single']?.[option.image] || '';
      if (step.key === 'hardware') return config.hardwareImages?.[option.image] || '';
      return '';
    };

    const openDialog = () => {
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else dialog.setAttribute('open', '');
      document.documentElement.classList.add('da-wizard-open');
    };

    const closeDialog = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      document.documentElement.classList.remove('da-wizard-open');
    };

    const getStep = (key) => steps.find((step) => step.key === key);
    const getAnswerLabel = (key) => {
      const step = getStep(key);
      return step?.options.find((option) => option.value === state.answers[key])?.label || 'Ej angivet';
    };

    const calculatePrice = () => {
      const opening = state.answers.opening || 'single';
      const model = state.answers.model || 'mahogany';
      const security = state.answers.security || 'rc3';
      const hardware = state.answers.hardware || 'standard';
      let includingVat = Number(config.pricesInclVat?.models?.[model]?.[opening] || 0);
      includingVat += Number(config.pricesInclVat?.security?.[security]?.[opening] || 0);
      includingVat += Number(config.pricesInclVat?.hardware?.[hardware] || 0);
      const vatRate = Number(config.vatRate || 25) / 100;
      const excludingVat = includingVat / (1 + vatRate);
      const active = state.vatMode === 'excl' ? excludingVat : includingVat;

      return {
        excludingVat: Math.round(excludingVat),
        includingVat: Math.round(includingVat),
        displayExcludingVat: formatSek(excludingVat),
        displayIncludingVat: formatSek(includingVat),
        display: formatSek(active),
        vatMode: state.vatMode,
        vatLabel: state.vatMode === 'excl' ? 'exkl. moms' : 'inkl. moms',
        perDoor: state.audience === 'brf'
      };
    };

    const buildResult = () => {
      const price = calculatePrice();
      return {
        version: 5,
        createdAt: new Date().toISOString(),
        audience: state.audience,
        audienceLabel: audienceLabels[state.audience],
        answers: { ...state.answers },
        labels: {
          opening: getAnswerLabel('opening'),
          security: getAnswerLabel('security'),
          model: getAnswerLabel('model'),
          hardware: getAnswerLabel('hardware')
        },
        price,
        previewImage: config.modelImages?.[state.answers.opening]?.[state.answers.model] || '',
        priceNote: config.priceNote,
        priceIncludes: config.priceIncludes,
        phone: config.phone,
        phoneLink: config.phoneLink
      };
    };

    const saveLeadState = () => {
      try {
        sessionStorage.setItem(leadKey, JSON.stringify({
          version: 5,
          audience: state.audience,
          stepIndex: state.stepIndex,
          answers: state.answers,
          vatMode: state.vatMode
        }));
      } catch (error) {}
    };

    const persist = () => {
      const result = buildResult();
      try {
        sessionStorage.setItem(resultKey, JSON.stringify(result));
        saveLeadState();
      } catch (error) {}
      return result;
    };

    const syncVatButtons = () => {
      el.vatButtons.forEach((button) => {
        const selected = button.dataset.daVatMode === state.vatMode;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (!el.contact.hidden) renderContactSummary();
    };

    const updateProgress = (stepNumber) => {
      el.progressItems.forEach((button, index) => {
        const itemStep = index + 1;
        const active = itemStep === stepNumber;
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-complete', itemStep < stepNumber);
        button.disabled = itemStep > stepNumber || itemStep === 5;
        if (active) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
    };

    const optionMarkup = (step, option) => {
      const image = imageFor(step, option);
      let media = '';
      if (image) {
        media = `<span class="da-door-wizard__option-media"><img src="${image}" alt="" loading="lazy"></span>`;
      } else if (option.icon) {
        media = `<span class="da-door-wizard__option-icon">${shieldMarkup(option.icon)}</span>`;
      } else {
        media = '<span class="da-door-wizard__option-media da-door-wizard__option-media--placeholder" aria-hidden="true"><i></i></span>';
      }
      return `${media}<span class="da-door-wizard__option-copy"><strong>${option.label}</strong>${option.text ? `<small>${option.text}</small>` : ''}</span><span class="da-door-wizard__check" aria-hidden="true">✓</span>`;
    };

    const selectAnswer = (step, option, button) => {
      state.answers[step.key] = option.value;
      el.options.querySelectorAll('.da-door-wizard__option').forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      el.next.disabled = false;
      saveLeadState();
    };

    const renderQuestion = () => {
      const step = steps[state.stepIndex];
      updateProgress(state.stepIndex + 1);
      el.question.hidden = false;
      el.contact.hidden = true;
      if (el.vatControl) el.vatControl.hidden = false;
      el.title.textContent = step.title;
      el.lead.textContent = step.lead;
      el.options.className = `da-door-wizard__options is-${step.layout}-step`;
      el.options.replaceChildren();

      step.options.forEach((option) => {
        const button = document.createElement('button');
        const selected = state.answers[step.key] === option.value;
        button.type = 'button';
        button.className = `da-door-wizard__option${selected ? ' is-selected' : ''}`;
        button.dataset.value = option.value;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.innerHTML = optionMarkup(step, option);
        button.addEventListener('click', () => selectAnswer(step, option, button));
        el.options.appendChild(button);
      });

      el.note.hidden = !step.note;
      el.note.textContent = step.note || '';
      el.back.textContent = state.stepIndex === 0 ? 'Avbryt' : 'Tillbaka';
      el.next.disabled = !state.answers[step.key];
      el.next.innerHTML = 'Nästa <span aria-hidden="true">→</span>';
      requestAnimationFrame(() => el.title.focus?.({ preventScroll: true }));
    };

    const renderContactSummary = () => {
      const result = buildResult();
      if (el.preview) {
        el.preview.innerHTML = `<span>Dina val</span><strong>Din dörr är vald</strong><small>${result.labels.opening} · ${result.labels.security} · ${result.labels.model} · ${result.labels.hardware}<br>Fyll i dina uppgifter så visas priset i nästa steg.</small>`;
      }
      if (el.summary) {
        el.summary.value = [
          'Lead från Dörrguiden',
          `Kundtyp: ${result.audienceLabel}`,
          `Dörrtyp: ${result.labels.opening}`,
          `Säkerhetsklass: ${result.labels.security}`,
          `Utförande: ${result.labels.model}`,
          `Beslag/lås: ${result.labels.hardware}`,
          `Vägledande frånpris: ${result.price.display} ${result.price.vatLabel}`,
          `Frånpris exkl. moms: ${result.price.displayExcludingVat}`,
          `Frånpris inkl. moms: ${result.price.displayIncludingVat}`
        ].join('\n');
      }
    };

    const renderContact = () => {
      updateProgress(5);
      el.question.hidden = true;
      el.contact.hidden = false;
      if (el.vatControl) el.vatControl.hidden = true;
      state.stepIndex = steps.length;
      renderContactSummary();
      saveLeadState();
      requestAnimationFrame(() => el.form?.querySelector('input[required]')?.focus?.({ preventScroll: true }));
    };

    const goBack = () => {
      if (!el.contact.hidden) {
        state.stepIndex = steps.length - 1;
        renderQuestion();
      } else if (state.stepIndex > 0) {
        state.stepIndex -= 1;
        renderQuestion();
      } else closeDialog();
    };

    const goNext = () => {
      const step = steps[state.stepIndex];
      if (!step || !state.answers[step.key]) return;
      if (state.stepIndex < steps.length - 1) {
        state.stepIndex += 1;
        renderQuestion();
      } else renderContact();
    };

    const restore = () => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(leadKey) || 'null');
        if (!saved || saved.version !== 5 || !audienceLabels[saved.audience]) return false;
        state.audience = saved.audience;
        state.answers = saved.answers || {};
        state.vatMode = saved.vatMode === 'excl' ? 'excl' : defaultVatMode;
        return true;
      } catch (error) { return false; }
    };

    el.starts.forEach((button) => button.addEventListener('click', () => {
      state.audience = audienceLabels[button.dataset.daWizardStart] ? button.dataset.daWizardStart : 'apartment';
      state.stepIndex = 0;
      state.answers = {};
      state.vatMode = defaultVatMode;
      try {
        sessionStorage.removeItem(leadKey);
        sessionStorage.removeItem(resultKey);
      } catch (error) {}
      syncVatButtons();
      renderQuestion();
      openDialog();
    }));

    el.guideFocus?.addEventListener('click', (event) => {
      event.preventDefault();
      el.guidePanel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => el.starts[0]?.focus(), 450);
    });
    el.close?.addEventListener('click', closeDialog);
    el.back?.addEventListener('click', goBack);
    el.contactBack?.addEventListener('click', goBack);
    el.next?.addEventListener('click', goNext);
    el.progressItems.forEach((button, index) => button.addEventListener('click', () => {
      if (index < Math.min(state.stepIndex, steps.length)) {
        state.stepIndex = index;
        renderQuestion();
      }
    }));
    el.vatButtons.forEach((button) => button.addEventListener('click', () => {
      state.vatMode = button.dataset.daVatMode === 'excl' ? 'excl' : 'incl';
      syncVatButtons();
      saveLeadState();
    }));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
    dialog.addEventListener('close', () => document.documentElement.classList.remove('da-wizard-open'));
    el.form?.addEventListener('submit', persist);

    syncVatButtons();
    if (el.errors && restore()) {
      syncVatButtons();
      renderContact();
      openDialog();
      requestAnimationFrame(() => el.errors.focus?.());
    } else {
      const url = new URL(window.location.href);
      if (url.searchParams.get('dorrguide') === '1') {
        state.audience = audienceLabels[url.searchParams.get('audience')] ? url.searchParams.get('audience') : 'apartment';
        renderQuestion();
        openDialog();
        url.searchParams.delete('dorrguide');
        url.searchParams.delete('audience');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    }
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-da-hero-guide]').forEach(initWizard);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
